import type { Project } from '../../project/types/project';
import type { PipeGroupNode, PipeGroupTreeNode } from './groupPipeMaterials';
import type { FitterSetupPipeSize } from './buildFitterSetupTree';

export type FitterNavLeafRef = {
  project: Project;
  pipe: FitterSetupPipeSize;
  groupId: string;
};

export function fitterProjectNodeId(projectId: string): string {
  return `fitter:project:${projectId}`;
}

export function fitterPipeNodeId(projectId: string, pipeId: string): string {
  return `fitter:pipe:${projectId}|${pipeId}`;
}

export function fitterGroupNodeId(
  projectId: string,
  pipeId: string,
  groupId: string
): string {
  return `fitter:group:${projectId}|${pipeId}|${groupId}`;
}

export function parseFitterGroupNodeId(
  id: string
): { projectId: string; pipeId: string; groupId: string } | null {
  if (!id.startsWith('fitter:group:')) return null;
  const rest = id.slice('fitter:group:'.length);
  const pipeSep = rest.indexOf('|');
  const groupSep = rest.indexOf('|', pipeSep + 1);
  if (pipeSep < 0 || groupSep < 0) return null;
  return {
    projectId: rest.slice(0, pipeSep),
    pipeId: rest.slice(pipeSep + 1, groupSep),
    groupId: rest.slice(groupSep + 1),
  };
}

/**
 * Build Material-style nav tree: Project → Pipe size → Group.
 * `groupCounts` optional map of fitterGroupNodeId → row total for badges.
 */
export function buildFitterNavTree(
  projects: Project[],
  pipesByProjectId: Record<string, FitterSetupPipeSize[]>,
  formatProjectLabel: (p: Project) => string,
  groupCounts?: Record<string, number>
): { tree: PipeGroupTreeNode[]; leafRefs: Record<string, FitterNavLeafRef> } {
  const leafRefs: Record<string, FitterNavLeafRef> = {};
  const tree: PipeGroupNode[] = [];

  for (const project of projects) {
    const projectId = String(project.projectId);
    const pipes = pipesByProjectId[projectId] ?? [];
    if (pipes.length === 0) continue;

    const pipeChildren: PipeGroupNode[] = [];
    let projectCount = 0;

    for (const pipe of pipes) {
      const groupChildren: PipeGroupNode[] = [];
      let pipeCount = 0;

      for (const groupId of pipe.groupIds) {
        const id = fitterGroupNodeId(projectId, pipe.pipeId, groupId);
        const count = groupCounts?.[id] ?? 0;
        pipeCount += count;
        leafRefs[id] = { project, pipe, groupId };
        groupChildren.push({
          type: 'group',
          id,
          field: 'pipeNo',
          key: groupId,
          label: groupId,
          count,
          depth: 2,
          children: [],
        });
      }

      if (groupChildren.length === 0) continue;
      // Use loaded totals when available; otherwise badge = child group count (browse before load).
      const pipeBadge = pipeCount > 0 ? pipeCount : groupChildren.length;
      projectCount += pipeBadge;
      pipeChildren.push({
        type: 'group',
        id: fitterPipeNodeId(projectId, pipe.pipeId),
        field: 'pipeId',
        key: pipe.pipeId,
        label: pipe.label,
        count: pipeBadge,
        depth: 1,
        children: groupChildren,
      });
    }

    if (pipeChildren.length === 0) continue;
    tree.push({
      type: 'group',
      id: fitterProjectNodeId(projectId),
      field: 'projectName',
      key: projectId,
      label: formatProjectLabel(project),
      count: projectCount > 0 ? projectCount : pipeChildren.length,
      depth: 0,
      children: pipeChildren,
    });
  }

  return { tree, leafRefs };
}

/** All deepest group node ids under a selected nav node (or all if null). */
export function collectFitterLeafGroupIds(
  tree: PipeGroupTreeNode[],
  selectedGroupId: string | null
): string[] {
  const out: string[] = [];

  const walk = (nodes: PipeGroupTreeNode[], collect: boolean) => {
    for (const node of nodes) {
      if (node.type !== 'group') continue;
      const here = collect || node.id === selectedGroupId;
      const hasGroupKids = node.children.some((c) => c.type === 'group');
      if (here && !hasGroupKids) {
        out.push(node.id);
      }
      walk(node.children, here);
    }
  };

  if (!selectedGroupId) {
    walk(tree, true);
  } else {
    walk(tree, false);
  }
  return out;
}
